import { formatCents, formatDecimal, parseMoneyToCents, tt } from '@agos/shared'

/**
 * §9.5's surcharges — *"per-product VAT picker + surcharge attachment in product editor"*, second half.
 *
 * A **záloha za obal** is 0,15 € on a can, refunded when the can comes back. `SurchargeService` charges it
 * as its own order line, multiplied by the quantity that carried it, so a receipt shows *2× Cola* and
 * *2× zálohovaný obal* and a report can tell the sale from the deposit. Five endpoints exist —
 * list, create, update, delete, attach — and **`surcharge_definitions` holds 0 rows**, because nothing has
 * ever been able to create one. Same shape as `/sales-channels` and `/vat-categories`: a finished backend
 * feature with no way in.
 *
 * **FB-187 — the only legally correct category is the one the till cannot print.**
 *
 * `create` refuses a surcharge without a `vatCategoryId`, and says why: *"záloha za obal je mimo DPH, ale
 * iné príplatky nemusia byť"*. A deposit is a refundable security rather than consideration for a supply,
 * so it is outside VAT under zák. 222/2004 Z. z. — and among this franchise's four categories the one that
 * expresses that is **D, *Oslobodené / mimo DPH*, 0 %**.
 *
 * `D` has **no `fiskal_slot`** (FB-183). `FiskalReceiptService.slotFor` refuses a receipt carrying a letter
 * whose index nobody has confirmed, rather than guessing — *"sending 23 % to a slot that is actually 0 %
 * registers the wrong tax and prints a receipt that looks perfectly correct"*. The surcharge's category is
 * snapshotted onto its line by `OrderCreationService.surchargeVat`, so the letter reaches the till.
 *
 * Therefore: **give a deposit the category the law asks for, and every product carrying it stops
 * printing.**
 *
 * FB-184 answered a very similar question by *withholding* the unprintable categories from the product
 * picker. This one deliberately does not, and the difference is the point. A product on a printable rate
 * is a legitimate product; a deposit on a printable rate is **the wrong tax on a refundable security**, and
 * choosing 5 % to keep the till quiet is registering tax that was never due. So the choice stays, the
 * consequence is stated where it is made, and the fix named is the missing index — which is a number
 * somebody reads off the device with `FRINFO`, not a decision about tax.
 */

export interface Surcharge {
  id: number
  name: string
  amountCents: number
  vatCategoryId: number | null
  isActive: boolean
  sortOrder: number
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

export function surchargesOf(payload: unknown): Surcharge[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        name: text(row.name) ?? '',
        amountCents: parseMoneyToCents(row.amount as string | number | null),
        vatCategoryId: typeof row.vatCategoryId === 'number' ? row.vatCategoryId : null,
        isActive: row.isActive !== false,
        sortOrder: typeof row.sortOrder === 'number' ? row.sortOrder : 0,
      },
    ]
  })
}

/** A VAT category as `/vat-categories` answers it, reduced to what this screen has to decide with. */
export interface CategoryChoice {
  id: number
  name: string
  letter: string
  ratePercent: number
  printable: boolean
  isActive: boolean
}

export function categoryChoicesOf(payload: unknown): CategoryChoice[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    const rate = row.rate
    return [
      {
        id: row.id,
        name: text(row.name) ?? '',
        letter: text(row.letter) ?? '',
        ratePercent: typeof rate === 'number' ? rate : Number(text(rate) ?? '0') || 0,
        printable: typeof row.fiskalSlot === 'number',
        isActive: row.isActive === true,
      },
    ]
  })
}

/**
 * Every switched-on category, printable or not — FB-187.
 *
 * Unlike the product picker, which withholds the unprintable ones. A product can honestly take a different
 * rate; a deposit cannot, because the alternative to *outside VAT* is tax that was never due.
 */
export function categoryChoices(choices: readonly CategoryChoice[]): CategoryChoice[] {
  return choices.filter((choice) => choice.isActive)
}

export function categoryLabel(choice: CategoryChoice): string {
  return `${choice.letter} - ${formatDecimal(choice.ratePercent, 2)} % · ${choice.name}`
}

export function chosenCategory(
  vatCategoryId: number | null,
  choices: readonly CategoryChoice[],
): CategoryChoice | null {
  if (vatCategoryId === null) return null
  return choices.find((choice) => choice.id === vatCategoryId) ?? null
}

/**
 * The consequence of the chosen category, said at the moment of choosing.
 *
 * Nothing here is a house rule: an unprintable letter is refused by `slotFor`, at the till, with the
 * customer waiting — and a surcharge is attached to products, so it takes them down with it.
 */
export function categoryWarning(chosen: CategoryChoice | null): string {
  if (chosen === null) return ''
  if (chosen.printable) return ''
  return `${tt('Sadzba')} ${chosen.letter} (${formatDecimal(chosen.ratePercent, 2)} %) ${tt('nemá potvrdený index na pokladni, takže doklad s produktom, ktorý má tento príplatok, sa nevytlačí. Je to správna sadzba pre zálohu za obal - doplňte jej index v Sadzbách DPH, nedávajte príplatku inú daň.')}`
}

/** Said once, above the form, because it is the reason the category cannot be left empty. */
export function categoryRequirementNote(): string {
  return tt(
    'Kategória DPH je povinná. Bez nej sa príplatok zdaní ako jedlo - a záloha za obal je mimo DPH, lebo je to vratná zábezpeka, nie predaj.',
  )
}

export interface Draft {
  name: string
  amount: string
  vatCategoryId: number | null
  isActive: boolean
  sortOrder: string
}

export const EMPTY_DRAFT: Draft = {
  name: '',
  amount: '',
  vatCategoryId: null,
  isActive: true,
  sortOrder: '0',
}

export function draftOf(surcharge: Surcharge): Draft {
  const whole = Math.trunc(surcharge.amountCents / 100)
  const rest = String(Math.abs(surcharge.amountCents % 100)).padStart(2, '0')
  return {
    name: surcharge.name,
    amount: `${whole},${rest}`,
    vatCategoryId: surcharge.vatCategoryId,
    isActive: surcharge.isActive,
    sortOrder: String(surcharge.sortOrder),
  }
}

const AMOUNT = /^\d+(?:[.,]\d{1,2})?$/

/** What the server would refuse, said before it is asked. Its three messages, in its order. */
export function problems(draft: Draft): string[] {
  const found: string[] = []
  if (draft.name.trim() === '') found.push(tt('Názov príplatku je povinný'))
  if (!AMOUNT.test(draft.amount.trim())) found.push(tt('Suma musí byť nezáporné číslo'))
  if (draft.vatCategoryId === null) {
    found.push(
      tt('Vyberte kategóriu DPH pre príplatok - záloha za obal je mimo DPH, ale iné príplatky nemusia byť'),
    )
  }
  return found
}

/** Cents back to euros: the column is `numeric(10,2)` and the endpoint reads a number. */
export function surchargePayload(draft: Draft): Record<string, unknown> {
  return {
    name: draft.name.trim(),
    amount: parseMoneyToCents(draft.amount) / 100,
    vatCategoryId: draft.vatCategoryId,
    isActive: draft.isActive,
    sortOrder: Number(draft.sortOrder) || 0,
  }
}

/**
 * Whether this surcharge can be deleted, or only switched off.
 *
 * `delete` refuses one that appears on any order line: *"deaktivuj ho namiesto mazania, inak sa stratí
 * história"*. The frontend cannot see the order lines, so it does not claim to know — it offers the
 * deletion, and when the server refuses, that refusal is the answer and is shown as one.
 */
export function deletionNote(): string {
  return tt(
    'Zmazať sa dá len príplatok, ktorý ešte nikdy nebol účtovaný. Ak už je na objednávkach, server zmazanie odmietne - vtedy ho deaktivujte, história zostane.',
  )
}

/** The line a receipt will carry, so the amount is checkable before anybody types it into a till. */
export function lineExample(draft: Draft, chosen: CategoryChoice | null): string {
  const cents = parseMoneyToCents(draft.amount)
  const name = draft.name.trim() === '' ? tt('príplatok') : draft.name.trim()
  const letter = chosen === null ? '' : ` · ${chosen.letter}`
  return `2× ${name} = ${formatCents(cents * 2)}${letter}`
}

/** Said above an empty list, because zero rows here means *nothing has ever created one*. */
export function emptyNote(): string {
  return tt(
    'Zatiaľ tu nie je žiadny príplatok. Backend ich vie účtovať ako samostatný riadok na doklade - zálohu za obal, balné a podobne - ale doteraz ich nemal kto založiť.',
  )
}
